import { useCallback, useEffect, useState } from "react";
import { KeyRound, BellRing } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/util";
import { enablePush } from "@/lib/push";
import { Pill } from "@/components/common";
import Modal from "@/components/Modal";

const ROLE = { admin: "مدير", staff: "موظف إدارة", driver: "سائق" };

function RequestRow({ r, onDone }) {
  const [pw, setPw] = useState("");
  const act = async (action) => {
    try {
      await api.post(`/admin/password-requests/${r.id}/resolve`, { action, password: pw });
      toast.success(action === "reset" ? `تم تعيين كلمة المرور الجديدة. أبلغ ${r.user_name} بها` : "تم إغلاق الطلب");
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className={`rounded-xl border p-3 space-y-2 ${r.status === "open" ? "border-red-200 bg-red-50/50" : "bg-slate-50"}`} data-testid={`pw-request-${r.id}`}>
      <div className="flex flex-wrap justify-between gap-2">
        <b>{r.user_name} <span className="text-sm text-slate-500">({ROLE[r.role]}{r.serial ? ` ${r.serial}` : ""})</span></b>
        <span className="text-sm text-slate-500">{fmtDateTime(r.created_at)}</span>
      </div>
      <div className="text-sm">الهاتف: <a href={`tel:${r.phone}`} className="ltr text-blue-700">{r.phone}</a>{r.note && <span> • ملاحظة: {r.note}</span>}</div>
      {r.status === "open" ? (
        <div className="flex flex-wrap gap-2">
          <input data-testid={`pw-request-input-${r.id}`} className="field field-sm flex-1 min-w-[160px]" placeholder="كلمة المرور الجديدة (6 أحرف على الأقل)" value={pw} onChange={(e) => setPw(e.target.value)} />
          <button data-testid={`pw-request-save-${r.id}`} disabled={pw.length < 6} onClick={() => act("reset")} className="btn btn-sm btn-blue h-11">تعيين</button>
          <button data-testid={`pw-request-dismiss-${r.id}`} onClick={() => act("dismiss")} className="btn btn-sm btn-ghost h-11">إغلاق</button>
        </div>
      ) : <Pill color={r.status === "resolved" ? "green" : "slate"}>{r.status === "resolved" ? `تم التعيين بواسطة ${r.resolved_by}` : "مغلق"} • {fmtDateTime(r.resolved_at)}</Pill>}
    </div>
  );
}

export default function PasswordRequests() {
  const [d, setD] = useState({ open: 0, items: [] });
  const [show, setShow] = useState(false);
  const load = useCallback(() => api.get("/admin/password-requests").then((r) => setD(r.data)).catch(() => {}), []);
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);
  const push = async () => {
    try { await enablePush(); toast.success("سيصلك تنبيه على هذا الجهاز عند أي طلب جديد"); } catch (e) { toast.error(e.message || errMsg(e)); }
  };
  return (
    <>
      <button data-testid="pw-requests-btn" onClick={() => { load(); setShow(true); }}
        className={`relative shrink-0 h-11 px-3 rounded-xl flex items-center gap-2 font-bold ${d.open ? "bg-red-600 text-white" : "bg-slate-100 text-slate-700"}`}>
        <KeyRound className="w-5 h-5" /><span className="hidden sm:inline">طلبات كلمة المرور</span>
        {d.open > 0 && <span data-testid="pw-requests-count" className="min-w-6 h-6 rounded-full bg-white text-red-600 text-sm flex items-center justify-center px-1">{d.open}</span>}
      </button>
      <Modal open={show} onClose={() => setShow(false)} title="طلبات استعادة كلمة المرور" testid="pw-requests-modal" wide>
        <button data-testid="admin-push-enable" onClick={push} className="btn btn-sm btn-ghost"><BellRing className="w-4 h-4" />تفعيل تنبيهات الطلبات على هذا الجهاز</button>
        <div className="space-y-3">
          {d.items.length === 0 && <p className="text-slate-500 text-center py-6">لا توجد طلبات</p>}
          {d.items.map((r) => <RequestRow key={r.id} r={r} onDone={load} />)}
        </div>
      </Modal>
    </>
  );
}
